<script lang="ts">
  // Submission form per INTERFACE-SPECIFICATION.md §7.
  //
  // Inputs: substantiation URL, effective-from (timezone-aware),
  // reviewer (auto-populated by value type), change description.
  // Pre-submission validation calls POST /api/validate; the user
  // sees the six §10.2 categories before they click Submit. Symmetry
  // warnings require an acknowledgement checkbox.

  import { onMount } from 'svelte';
  import { api, ApiError } from '../lib/api';
  import { accessControlState, loadAccessControlSnapshot, reviewersWithRole } from '../lib/access-control-store.svelte';
  import {
    stagedEditsState,
    clearEditing,
    flushSave
  } from '../lib/staged-edits.svelte';
  import { stagedEditsToPayload } from '../lib/staged-edits';
  import { reviewerRoleFor } from '../lib/roles';
  import type {
    ProductCatalogueFile,
    ValidationResult,
    ValueType,
    ProposeResponse,
    DownstreamConflict,
    DownstreamQueueAcknowledgement
  } from '../lib/types';
  import EffectiveFromPicker from './EffectiveFromPicker.svelte';
  import PurposeAmountRangeDelta from './PurposeAmountRangeDelta.svelte';
  import { formatRate, formatAmount, formatRateDelta, deltaCssClass } from '../lib/formatters';
  import { resolveLocalToUtc } from '../lib/timezone';

  interface Props {
    catalogue: ProductCatalogueFile;
    valueType: ValueType;
    onClose: () => void;
    onSubmitted: (response: ProposeResponse) => void;
  }

  const { catalogue, valueType, onClose, onSubmitted }: Props = $props();

  const timezone = $derived(catalogue._jurisdiction_metadata.timezone);

  let substantiation = $state<string>('');
  let localIso = $state<string>(defaultEffectiveFrom());
  let reviewerEmail = $state<string>('');
  let changeDescription = $state<string>('');
  let ambiguousChoice = $state<{ utc: string; abbreviation: string; occurrence: 'first' | 'second' } | null>(null);
  let warningAcknowledged = $state<boolean>(false);
  // Release-now toggle. When true, the date+timezone picker is hidden
  // and the submit payload sets release_now: true so the server stamps
  // effective_from at the submission instant in the cube jurisdiction
  // timezone. The merge-time clamp at manifest-update.mjs handles the
  // delay between submission and merge — see PR 3.2.2 commit 1 and
  // GOVERNANCE-ARCHITECTURE.md §4.3. Default to Schedule so the
  // deliberate path is first-class.
  let releaseNow = $state<boolean>(false);

  let validationResult = $state<ValidationResult | null>(null);
  let validationError = $state<string | null>(null);
  let validating = $state<boolean>(false);
  let submitting = $state<boolean>(false);
  let submissionError = $state<string | null>(null);

  // INTERFACE-SPECIFICATION.md §7.4. When the proposer's effective_from
  // is earlier than one or more `approved` queued manifest entries on
  // the same (jurisdiction, product), the queued snapshots will
  // silently overwrite cells the new change edits when their time-gate
  // fires. Detect at submit-time, surface the warning panel, and
  // require an acknowledgement checkbox before Submit becomes enabled.
  // The acknowledgement is logged on the audit record alongside the
  // submission (the server stamps it on _governance.last_proposal and
  // appends a section to the PR body).
  let downstreamConflicts = $state<DownstreamConflict[]>([]);
  let downstreamAcknowledged = $state<boolean>(false);
  let detectingDownstream = $state<boolean>(false);
  let downstreamError = $state<string | null>(null);

  function defaultEffectiveFrom(): string {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 14);
    const y = tomorrow.getFullYear();
    const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const d = String(tomorrow.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + d + 'T00:00:00';
  }

  const reviewerRole = $derived(reviewerRoleFor(valueType));
  const eligibleReviewers = $derived(reviewersWithRole(reviewerRole));

  onMount(async () => {
    await loadAccessControlSnapshot();
    if (!reviewerEmail && eligibleReviewers.length > 0) {
      reviewerEmail = eligibleReviewers[0]!.email;
    }
    void runValidation();
  });

  $effect(() => {
    if (!reviewerEmail && eligibleReviewers.length > 0) {
      reviewerEmail = eligibleReviewers[0]!.email;
    }
  });

  async function runValidation() {
    if (!stagedEditsState.jurisdiction || !stagedEditsState.product) return;
    validating = true;
    validationError = null;
    try {
      // lint:edit-category-partial-ok — cube_edits is read from `stagedEditsState.edits` per the SPA store's legacy field name.
      const snapshot = {
        jurisdiction: stagedEditsState.jurisdiction,
        product: stagedEditsState.product,
        draftSlug: stagedEditsState.draftSlug ?? '',
        valueType: stagedEditsState.valueType,
        edits: stagedEditsState.edits,
        amount_edits: stagedEditsState.amount_edits,
        purpose_edits: stagedEditsState.purpose_edits,
        fee_edits: stagedEditsState.fee_edits,
        fee_override_edits: stagedEditsState.fee_override_edits,
        term_edits: stagedEditsState.term_edits,
        lastObservedCommitSha: stagedEditsState.lastObservedCommitSha
      };
      const payload = stagedEditsToPayload(snapshot);
      const result = await api.validate({
        ...payload,
        reviewer: reviewerEmail || undefined
      });
      validationResult = result.validation_result;
    } catch (err) {
      validationError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
        ? err.message
        : 'validation_failed';
    } finally {
      validating = false;
    }
  }

  const hasDeclaredAsymmetries = $derived((validationResult?.symmetryDeclared?.length ?? 0) > 0);

  // Resolve the picker selection to a UTC instant for the downstream
  // conflict check. The check needs `proposed_effective_from_utc` to
  // filter queued entries to those with later effective_from. The
  // server resolves the canonical UTC at proposeChange time, so this
  // SPA-side resolution is an approximation used only for the check.
  // Release-now uses "now" — queued entries in the future remain
  // downstream regardless of the few-second delta between this check
  // and the actual submission instant.
  const proposedEffectiveFromUtc = $derived.by(() => {
    if (releaseNow) return new Date().toISOString();
    if (!localIso || !timezone) return null;
    if (ambiguousChoice) return ambiguousChoice.utc;
    const resolved = resolveLocalToUtc(localIso, timezone);
    return resolved.status === 'ok' ? resolved.utc ?? null : null;
  });

  // The detection runs whenever effective_from, edits, or jurisdiction
  // /product change. Schema or symmetry failures do not block the
  // check — even if the new change would not pass validation, the
  // proposer still sees the downstream-queue hazard for the cells
  // they have edited.
  $effect(() => {
    const utc = proposedEffectiveFromUtc;
    const jurisdiction = stagedEditsState.jurisdiction;
    const product = stagedEditsState.product;
    if (!utc || !jurisdiction || !product) {
      downstreamConflicts = [];
      downstreamAcknowledged = false;
      return;
    }
    const snapshot = {
      jurisdiction,
      product,
      draftSlug: stagedEditsState.draftSlug ?? '',
      valueType: stagedEditsState.valueType,
      edits: stagedEditsState.edits,
      amount_edits: stagedEditsState.amount_edits,
      purpose_edits: stagedEditsState.purpose_edits,
      fee_edits: stagedEditsState.fee_edits,
      fee_override_edits: stagedEditsState.fee_override_edits,
      term_edits: stagedEditsState.term_edits,
      lastObservedCommitSha: stagedEditsState.lastObservedCommitSha
    };
    const payload = stagedEditsToPayload(snapshot);
    const hasAnyEdits = (payload.cube_edits?.length ?? 0)
      + (payload.fee_edits?.length ?? 0)
      + (payload.fee_override_edits?.length ?? 0)
      + (payload.purpose_edits?.length ?? 0)
      + (payload.amount_edits?.length ?? 0)
      + (payload.term_edits?.length ?? 0)
      > 0;
    if (!hasAnyEdits) {
      downstreamConflicts = [];
      downstreamAcknowledged = false;
      return;
    }
    detectingDownstream = true;
    downstreamError = null;
    api.detectDownstreamConflict({
      jurisdiction,
      product,
      proposed_effective_from_utc: utc,
      cube_edits: payload.cube_edits,
      fee_edits: payload.fee_edits,
      fee_override_edits: payload.fee_override_edits,
      purpose_edits: payload.purpose_edits,
      amount_edits: payload.amount_edits,
      term_edits: payload.term_edits
    })
      .then((response) => {
        downstreamConflicts = response.conflicts;
        // Reset acknowledgement when the conflict set changes so the
        // user must re-acknowledge if the conflicts list expands.
        downstreamAcknowledged = false;
      })
      .catch((err) => {
        downstreamError = err instanceof ApiError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
          ? err.message
          : 'downstream_check_failed';
        downstreamConflicts = [];
      })
      .finally(() => {
        detectingDownstream = false;
      });
  });

  const hasDownstreamConflicts = $derived(downstreamConflicts.length > 0);

  const submitDisabled = $derived(
    submitting
    || validating
    || detectingDownstream
    || !substantiation.startsWith('http')
    || !reviewerEmail
    // Schedule path requires an effective-from instant; Release-now
    // bypasses the picker so we do not gate on localIso.
    || (!releaseNow && !localIso)
    || (validationResult?.symmetryFailures.length ?? 0) > 0
    || (validationResult?.schemaErrors.length ?? 0) > 0
    || (hasDeclaredAsymmetries && !warningAcknowledged)
    || (hasDownstreamConflicts && !downstreamAcknowledged)
  );

  async function handleSubmit() {
    if (!stagedEditsState.draftSlug) return;
    submitting = true;
    submissionError = null;
    try {
      await flushSave(); // ensure the latest edits are persisted server-side
      const payload: Parameters<typeof api.promoteDraft>[1] = {
        substantiation_url: substantiation,
        reviewer: reviewerEmail,
        change_description: changeDescription
      };
      if (releaseNow) {
        payload.release_now = true;
      } else {
        payload.effective_from_local = localIso;
        payload.effective_from_timezone = timezone;
        // On DST fall-back dates the picker collects which of the two
        // occurrences the user intended; forward it so the server
        // resolves the same UTC instant the user saw on screen. The
        // server ignores the field on unambiguous local times.
        if (ambiguousChoice) {
          payload.effective_from_occurrence = ambiguousChoice.occurrence;
        }
      }
      // Audit trail for §7.4: when the proposer has acknowledged that
      // queued downstream changes would overwrite cells in this edit
      // set, capture the conflicts they saw plus the timestamp of the
      // acknowledgement. The server stamps this on the cube's
      // _governance.last_proposal and adds a section to the PR body
      // so the post-hoc record shows the warning was surfaced.
      if (downstreamAcknowledged && downstreamConflicts.length > 0) {
        const ack: DownstreamQueueAcknowledgement = {
          conflicts: downstreamConflicts.map((c) => ({
            catalogue_version: c.catalogue_version,
            overlapping_cells_count: c.overlapping_cells.length
          })),
          acknowledged_at: new Date().toISOString()
        };
        payload.downstream_queue_acknowledged = ack;
      }
      const response = await api.promoteDraft(stagedEditsState.draftSlug, payload);
      clearEditing();
      onSubmitted(response);
    } catch (err) {
      submissionError = err instanceof ApiError
        ? `${err.code}: ${err.message}`
        : err instanceof Error
        ? err.message
        : 'submission_failed';
    } finally {
      submitting = false;
    }
  }

  function handleAmbiguous(utc: string, abbreviation: string, occurrence: 'first' | 'second') {
    ambiguousChoice = { utc, abbreviation, occurrence };
  }

  // lint:edit-category-partial-ok — cubeEdits reads stagedEditsState.edits per the SPA store's legacy field name; the six derivations below cover all six EDIT_CATEGORIES.
  const cubeEdits = $derived(Object.values(stagedEditsState.edits));
  const amountEdits = $derived(Object.values(stagedEditsState.amount_edits));
  const purposeEdits = $derived(Object.values(stagedEditsState.purpose_edits));
  const feeEdits = $derived(Object.values(stagedEditsState.fee_edits));
  const feeOverrideEdits = $derived(Object.values(stagedEditsState.fee_override_edits));
  const termEdit = $derived(stagedEditsState.term_edits);

  // Mirrors describeOverride in CubeEditor.svelte and formatOverride in
  // CubeTable.svelte (B-lite shorthand: scalar dollar for single-tier;
  // 'varies (N tiers)' for multi-tier; em-dash for an absent or removed
  // override). Re-stating inline keeps the SubmitForReview render
  // independent of the editor components.
  function describeOverride(override: import('./../lib/types').EstablishmentFeeOverride | null | undefined): string {
    if (!override || !Array.isArray(override.tiers) || override.tiers.length === 0) return '—';
    if (override.tiers.length === 1) {
      const fee = override.tiers[0]?.fee;
      return typeof fee === 'number' ? `$${formatAmount(fee)}` : '—';
    }
    return `varies (${override.tiers.length} tiers)`;
  }
  const termsAdded = $derived(
    termEdit ? termEdit.staged.filter((t) => !termEdit.before.includes(t)) : []
  );
  const termsRemoved = $derived(
    termEdit ? termEdit.before.filter((t) => !termEdit.staged.includes(t)) : []
  );
</script>

<section class="submit-form card">
  <header>
    <h2>Submit changes for review</h2>
    <button class="btn-link" type="button" onclick={onClose}>Cancel</button>
  </header>

  {#if valueType === 'rates'}
    <h3 class="section-heading">Cube cell changes</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>Coordinates</th>
          <th class="num">% before</th>
          <th class="num">% after</th>
          <th class="num">Δ %</th>
        </tr>
      </thead>
      <tbody>
        {#each cubeEdits as edit (edit.coordinates)}
          {@const before = edit.before.interest_rate_pa}
          {@const after = edit.staged.interest_rate_pa ?? before}
          {@const delta = before !== null && after !== null ? Number(after) - Number(before) : null}
          <tr>
            <td><code>{edit.coordinates}</code></td>
            <td class="num">{formatRate(before)}</td>
            <td class="num">{formatRate(after)}</td>
            <td class="num {deltaCssClass(delta)}">{formatRateDelta(delta)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else if valueType === 'amounts'}
    <h3 class="section-heading">Loan amount range changes</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>Field</th>
          <th class="num">$ before</th>
          <th class="num">$ after</th>
          <th class="num">Δ $</th>
        </tr>
      </thead>
      <tbody>
        {#each amountEdits as edit (edit.field)}
          {@const delta = edit.staged - edit.before}
          <tr>
            <td><strong>{edit.field === 'min' ? 'Minimum' : edit.field === 'max' ? 'Maximum' : 'Increment'}</strong></td>
            <td class="num">${formatAmount(edit.before)}</td>
            <td class="num">${formatAmount(edit.staged)}</td>
            <td class="num {deltaCssClass(delta)}">{delta > 0 ? '+' : ''}{formatAmount(delta)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else if valueType === 'fees'}
    <h3 class="section-heading">Fee schedule changes</h3>
    {#if feeEdits.length === 0 && feeOverrideEdits.length === 0}
      <p class="muted">No fee changes staged.</p>
    {/if}
    {#if feeEdits.length > 0}
      <table class="cube-table">
        <thead>
          <tr>
            <th>Path</th>
            <th>Before</th>
            <th>After</th>
          </tr>
        </thead>
        <tbody>
          {#each feeEdits as edit (edit.path)}
            <tr>
              <td><code>{edit.path.replace(/^fees\./, '')}</code></td>
              <td><code class="diff-payload">{JSON.stringify(edit.before)}</code></td>
              <td><code class="diff-payload">{JSON.stringify(edit.staged)}</code></td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
    {#if feeOverrideEdits.length > 0}
      <h4 class="section-heading">Establishment-fee overrides</h4>
      <table class="cube-table">
        <thead>
          <tr>
            <th>Credit rating</th>
            <th>Before</th>
            <th>After</th>
          </tr>
        </thead>
        <tbody>
          {#each feeOverrideEdits as edit (edit.credit_rating_id)}
            <tr>
              <td><code>rate_cube.{edit.credit_rating_id}.establishment_fee_override</code></td>
              <td>{describeOverride(edit.before)}</td>
              <td>{describeOverride(edit.staged)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  {:else if valueType === 'purposes'}
    <h3 class="section-heading">Loan purpose changes</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>Action</th>
          <th>ID</th>
          <th>Display name</th>
          <th>Amount range</th>
        </tr>
      </thead>
      <tbody>
        {#each purposeEdits as edit (edit.purpose_id)}
          <tr>
            <td><strong>{edit.action.charAt(0).toUpperCase() + edit.action.slice(1)}</strong></td>
            <td><code>{edit.purpose_id}</code></td>
            <td>
              {#if edit.action === 'remove'}
                <span class="muted">{edit.before.display_name}</span>
              {:else if edit.action === 'update'}
                {edit.before.display_name !== edit.staged.display_name
                  ? `${edit.before.display_name} → ${edit.staged.display_name}`
                  : edit.staged.display_name}
              {:else}
                {edit.staged.display_name}
              {/if}
            </td>
            <td>
              {#if edit.action === 'remove'}
                <span class="muted">
                  <PurposeAmountRangeDelta before={edit.before.amount_range ?? null} after={null} />
                </span>
              {:else if edit.action === 'update'}
                <PurposeAmountRangeDelta before={edit.before.amount_range ?? null} after={edit.staged.amount_range ?? null} />
              {:else}
                <PurposeAmountRangeDelta before={null} after={edit.staged.amount_range ?? null} />
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else if valueType === 'terms'}
    <h3 class="section-heading">Available-terms changes</h3>
    {#if termEdit}
      <table class="cube-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Before</th>
            <th>After</th>
            <th>Δ</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>terms_months</strong></td>
            <td><code class="diff-payload">{JSON.stringify(termEdit.before)}</code></td>
            <td><code class="diff-payload">{JSON.stringify(termEdit.staged)}</code></td>
            <td>
              {#if termsAdded.length > 0}<span class="delta-up">+{termsAdded.join(', ')}</span>{/if}
              {#if termsAdded.length > 0 && termsRemoved.length > 0}{' '}{/if}
              {#if termsRemoved.length > 0}<span class="delta-down">−{termsRemoved.join(', ')}</span>{/if}
            </td>
          </tr>
        </tbody>
      </table>
    {/if}
  {/if}

  <label class="form-row">
    <span>Substantiation *</span>
    <input
      type="url"
      placeholder="https://harmoney.atlassian.net/wiki/…"
      bind:value={substantiation}
    />
    <span class="form-hint">Link to the Confluence page that documents the business rationale.</span>
  </label>

  <div class="form-row">
    <span class="form-row__label">Effective-from *</span>
    <div class="release-mode-toggle" role="radiogroup" aria-label="Release timing">
      <label class="release-mode-option" class:is-active={!releaseNow}>
        <input
          type="radio"
          name="release-mode"
          value="schedule"
          checked={!releaseNow}
          onchange={() => (releaseNow = false)}
        />
        <span>Schedule for later</span>
      </label>
      <label class="release-mode-option" class:is-active={releaseNow}>
        <input
          type="radio"
          name="release-mode"
          value="now"
          checked={releaseNow}
          onchange={() => (releaseNow = true)}
        />
        <span>Release now</span>
      </label>
    </div>
    {#if !releaseNow}
      <EffectiveFromPicker
        {timezone}
        {localIso}
        onLocalIso={(v) => { localIso = v; ambiguousChoice = null; }}
        onAmbiguousChoice={handleAmbiguous}
      />
    {:else}
      <p class="form-hint release-now-hint">
        The change will go live the moment your reviewer approves and
        the merge completes. The audit trail records both the submission
        instant (your intent) and the merge instant (when customers
        actually saw the new value).
      </p>
    {/if}
  </div>

  <label class="form-row">
    <span>Reviewer *</span>
    {#if accessControlState.loading}
      <span class="muted">Loading reviewers…</span>
    {:else}
      <select bind:value={reviewerEmail}>
        <option value="">Select a reviewer…</option>
        {#each eligibleReviewers as r (r.email)}
          <option value={r.email}>{r.display_name ?? r.email} — {r.email}</option>
        {/each}
      </select>
      <span class="form-hint">Auto-populated for value type {valueType}. Required role: {reviewerRole}.</span>
    {/if}
  </label>

  <label class="form-row">
    <span>Change description</span>
    <textarea rows="3" bind:value={changeDescription} placeholder="Brief summary for the release history."></textarea>
  </label>

  <section class="validation-card">
    <h3 class="section-heading">Pre-submission validation</h3>
    {#if validating}
      <p class="muted">Validating…</p>
    {:else if validationError}
      <p class="status-fail">{validationError}</p>
    {:else if validationResult}
      <ul class="validation-list">
        <li class="status-pass">✓ Schema: {validationResult.schemaErrors.length === 0 ? 'passed' : `${validationResult.schemaErrors.length} error(s)`}</li>
        <li class:status-pass={validationResult.symmetryFailures.length === 0} class:status-fail={validationResult.symmetryFailures.length > 0}>
          {validationResult.symmetryFailures.length === 0 ? '✓' : '✗'} Symmetry: {validationResult.symmetryFailures.length} undeclared failure(s)
        </li>
        <li class:status-warn={validationResult.symmetryDeclared.length > 0}>
          ⚠ Declared asymmetries: {validationResult.symmetryDeclared.length}
        </li>
      </ul>
      {#if validationResult.symmetryFailures.length > 0}
        <!-- Enumerate the symmetry failures by category + label so the
             proposer can see which rule fired. The override symmetry
             rule (GOVERNANCE-ARCHITECTURE.md §10.2 + check-symmetry.mjs
             checkEstablishmentFeeOverride) fires here when an
             override sits on a non-canonical path or carries a malformed
             tier shape; the proposer needs the message to act. -->
        <ul class="symmetry-failures">
          {#each validationResult.symmetryFailures as entry}
            <li>
              <strong>{entry.category}</strong>
              <span class="muted"> — {entry.label}</span>
            </li>
          {/each}
        </ul>
      {/if}
      {#if hasDeclaredAsymmetries}
        <!-- Enumerate the declared asymmetries so the proposer is
             explicit about what is being acknowledged, not just a
             headline count. The reviewer sees the same enumeration on
             their side (ReviewInterface). Unkeyed each — the list is
             small, and rebuilds entirely on every validation re-run. -->
        <ul class="declared-asymmetries">
          {#each validationResult.symmetryDeclared as entry}
            <li>
              <strong>{entry.category}</strong>
              <span class="muted"> — {entry.label}</span>
            </li>
          {/each}
        </ul>
        <label class="ack">
          <input type="checkbox" bind:checked={warningAcknowledged} />
          Yes, the declared asymmetries above are intentional.
        </label>
      {/if}
    {/if}
    <button class="btn-link" type="button" onclick={runValidation}>Re-run validation</button>
  </section>

  <!-- INTERFACE-SPECIFICATION.md §7.4 downstream-queue conflict panel.
       Surfaces queued manifest entries whose snapshots would silently
       overwrite the cells edited by this change. Required acknowledgement
       gates Submit; the ack is logged on the audit record via the
       downstream_queue_acknowledged field on the submission payload. -->
  {#if detectingDownstream}
    <section class="downstream-card downstream-card--checking">
      <p class="muted">Checking downstream queued changes…</p>
    </section>
  {:else if downstreamError}
    <section class="downstream-card downstream-card--error">
      <p class="status-fail">Downstream-queue check failed: {downstreamError}</p>
    </section>
  {:else if hasDownstreamConflicts}
    <section class="downstream-card" role="alert">
      <h3 class="downstream-card__heading">
        <span aria-hidden="true">⚠</span>
        Downstream-queue conflict
      </h3>
      <p>
        {downstreamConflicts.length} queued change{downstreamConflicts.length === 1 ? ' is' : 's are'}
        scheduled for after your effective-from. The frozen snapshot{downstreamConflicts.length === 1 ? '' : 's'}
        will overwrite the cells you are editing when {downstreamConflicts.length === 1 ? 'it activates' : 'they activate'}.
      </p>
      <ul class="downstream-list">
        {#each downstreamConflicts as conflict (conflict.catalogue_version)}
          <li class="downstream-list__item">
            <p class="downstream-list__title">
              <code>{conflict.catalogue_version}</code>
              {#if conflict.value_type}
                <span class="muted"> · {conflict.value_type}</span>
              {/if}
            </p>
            <p class="downstream-list__meta muted">
              {#if conflict.approved_by}Approved by {conflict.approved_by} ·{/if}
              effective {conflict.effective_from.local} {conflict.effective_from.tz_abbreviation_at_effective}
            </p>
            {#if conflict.change_summary}
              <p class="downstream-list__summary"><em>{conflict.change_summary}</em></p>
            {/if}
            <p class="downstream-list__overlap muted">
              Overlaps your edit{conflict.overlapping_cells.length === 1 ? '' : 's'} to:
            </p>
            <ul class="downstream-cells">
              {#each conflict.overlapping_cells as cell}
                <li>
                  <code>{cell.coordinates}</code>
                  {#if cell.edit_type !== 'cube'}
                    <span class="muted"> ({cell.edit_type}{cell.action ? ` ${cell.action}` : ''})</span>
                  {/if}
                </li>
              {/each}
            </ul>
          </li>
        {/each}
      </ul>
      <label class="ack">
        <input type="checkbox" bind:checked={downstreamAcknowledged} />
        I understand that the queued change{downstreamConflicts.length === 1 ? '' : 's'} above will overwrite my edits when {downstreamConflicts.length === 1 ? 'it activates' : 'they activate'}, unless {downstreamConflicts.length === 1 ? 'it is' : 'they are'} revised first.
      </label>
    </section>
  {/if}

  {#if submissionError}
    <div class="banner banner-fail">{submissionError}</div>
  {/if}

  <p class="muted submit-form__archive-note">
    <strong>Heads up:</strong> Submitting will move this draft into your
    archive. The proposal PR is now the canonical artefact for this
    change — you will track it under <em>Pending review</em> (or your
    proposer view) rather than continuing to edit the draft. If a
    reviewer requests changes, open the Revise interface from the
    proposal card to iterate on the same PR. The archive entry remains
    as read-only history for 90 days for audit; closed drafts cannot
    be restored, but you can begin a new draft from an archived entry
    via the My Drafts → Archive surface.
  </p>

  <footer class="submit-form__footer">
    <button class="btn" type="button" onclick={onClose}>Cancel</button>
    <button class="btn btn-primary" type="button" disabled={submitDisabled} onclick={handleSubmit}>
      {submitting ? 'Submitting…' : 'Submit for review →'}
    </button>
  </footer>
</section>

<style>
  .submit-form {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .submit-form header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .validation-card { display: flex; flex-direction: column; gap: var(--space-2); }
  .validation-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-1); font-family: var(--font-mono); font-size: var(--type-sm); }
  .declared-asymmetries,
  .symmetry-failures {
    list-style: disc;
    padding-left: var(--space-4);
    margin: var(--space-1) 0 0 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--type-sm);
  }
  .symmetry-failures {
    color: var(--accent-fail);
  }
  .ack { display: inline-flex; gap: var(--space-2); margin-top: var(--space-2); }

  /* §7.4 downstream-queue conflict panel — amber accent, distinct
     from the declared-asymmetry warning so the operator does not
     confuse "you have a queued change that will overwrite this" with
     the existing within-proposal symmetry-warning surface. */
  .downstream-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3);
    border: 1px solid var(--color-warn, #b45309);
    border-left-width: 4px;
    border-radius: var(--radius-sm);
    background: var(--surface-card);
  }
  .downstream-card--checking,
  .downstream-card--error {
    border-color: var(--surface-line);
    border-left-width: 1px;
  }
  .downstream-card__heading {
    margin: 0;
    color: var(--color-warn, #b45309);
    font-size: var(--type-md);
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .downstream-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .downstream-list__item {
    padding: var(--space-2);
    border-left: 2px solid var(--surface-line);
  }
  .downstream-list__title {
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--type-sm);
    font-weight: 600;
  }
  .downstream-list__meta,
  .downstream-list__summary,
  .downstream-list__overlap {
    margin: var(--space-1) 0 0 0;
    font-size: var(--type-sm);
  }
  .downstream-cells {
    list-style: disc;
    margin: var(--space-1) 0 0 0;
    padding-left: var(--space-4);
    font-size: var(--type-sm);
    font-family: var(--font-mono);
  }
  .downstream-cells code {
    font-family: inherit;
  }
  /* Release timing toggle: Schedule (default) vs Release now. */
  .release-mode-toggle {
    display: inline-flex;
    gap: 0;
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-sm);
    overflow: hidden;
    margin-bottom: var(--space-2);
  }
  .release-mode-option {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3);
    cursor: pointer;
    background: transparent;
    font-size: var(--type-sm);
    color: var(--surface-muted);
  }
  .release-mode-option + .release-mode-option {
    border-left: 1px solid var(--surface-line);
  }
  .release-mode-option.is-active {
    background: var(--surface-card);
    color: var(--surface-fg);
    font-weight: 500;
  }
  .release-mode-option input[type="radio"] {
    accent-color: var(--surface-fg);
  }
  .release-now-hint {
    margin: 0;
    font-style: italic;
  }
  .submit-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
    padding-top: var(--space-3);
    border-top: 1px solid var(--surface-line-soft);
  }
  .submit-form__archive-note {
    font-size: var(--type-sm);
    background: var(--surface-card);
    border-left: 3px solid var(--surface-line);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    margin: 0;
  }
  code { font-family: var(--font-mono); }
  /* Fees-summary cells render a JSON snippet (the path's whole value)
     so the reviewer can see exactly what is changing. Wrap aggressively
     so a wide tiers array does not blow out the table layout. */
  code.diff-payload {
    display: inline-block;
    max-width: 32rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: var(--type-xs);
  }
</style>
